/**
 * ConfigEditor — Route handler
 *
 * Entry points:
 * - /config (no versions exist)  → TemplateChooser → ConfigWizard
 * - /config (versions exist)     → ConfigFocusedEditor on active version (read mode)
 * - /config/history/:id (draft)  → ConfigFocusedEditor in edit mode
 * - /config/history/:id (published) → ConfigFocusedEditor read mode, "Edit" creates draft
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import TemplateChooser from './TemplateChooser';
import ConfigWizard from './ConfigWizard';
import ConfigFocusedEditor from './ConfigFocusedEditor';
import useConfigDraft from './hooks/useConfigDraft';
import { configToForm, EMPTY_PROCESS } from './utils/configTransforms';

export default function ConfigEditor() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: versionId } = useParams();

  // When a draft is created by clicking "Edit" on a published version, we
  // navigate() to the new draft's URL and pass the source version number in
  // route state. useConfigDraft seeds draftCreatedFrom from this so the
  // "Editing created a new draft" banner renders on the remounted page.
  const initialDraftCreatedFrom = location.state?.sourceVersionNumber || null;

  const draft = useConfigDraft(versionId, initialDraftCreatedFrom);
  const { loading, error, version, hasVersions, isDraft,
          processes, setProcesses, createDraftFromVersion } = draft;

  // UI mode state
  const [mode, setMode] = useState(null); // 'template' | 'wizard' | 'editor'
  const [isEditing, setIsEditing] = useState(false);

  // Determine mode based on route and data
  useEffect(() => {
    if (loading) return;

    if (!hasVersions) {
      // No versions exist — first-time setup
      setMode('template');
    } else {
      // Either /config (active version loaded by hook) or /config/history/:id
      setMode('editor');
      setIsEditing(isDraft); // drafts auto-enter edit mode
    }
  }, [loading, hasVersions, isDraft]);

  // Template selected → initialize wizard
  function handleTemplateSelect(template) {
    if (template) {
      // Pre-fill from template
      setProcesses(configToForm(template.config));
    } else {
      // Start from scratch
      setProcesses([EMPTY_PROCESS()]);
    }
    setMode('wizard');
  }

  // Start editing a published version
  async function handleStartEdit() {
    if (version && version.published_at) {
      const sourceVersionNumber = version.version_number;
      const newId = await createDraftFromVersion(version.id);
      if (newId) {
        navigate(`/config/history/${newId}`, {
          replace: true,
          state: { sourceVersionNumber },
        });
      }
    } else {
      // Already a draft
      setIsEditing(true);
    }
  }

  // --- Loading state ---
  if (loading) {
    return (
      <div className="page">
        <div style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>Loading…</div>
      </div>
    );
  }

  // --- Error with no data ---
  if (error && !version && mode !== 'template' && mode !== 'wizard') {
    return (
      <div className="page">
        <div className="alert alert-error">{error}</div>
      </div>
    );
  }

  // --- Template chooser ---
  if (mode === 'template') {
    return <TemplateChooser onSelect={handleTemplateSelect} />;
  }

  // --- Wizard ---
  if (mode === 'wizard' && processes) {
    return (
      <ConfigWizard
        processes={processes}
        setProcesses={setProcesses}
        onSaveDraft={async () => {
          // For wizard, we need to create the version first, then save
          const { formToConfig } = await import('./utils/configTransforms');
          const config = formToConfig(processes);
          try {
            const api = (await import('../../api/client')).default;
            await api.post('/portal/config/versions', { config });
            return true;
          } catch (err) {
            return false;
          }
        }}
        onSavePublish={async () => {
          const { formToConfig } = await import('./utils/configTransforms');
          const config = formToConfig(processes);
          try {
            const api = (await import('../../api/client')).default;
            const created = await api.post('/portal/config/versions', { config });
            await api.put(`/portal/config/versions/${created.data.id}/publish`);
            return true;
          } catch (err) {
            return false;
          }
        }}
        saving={draft.saving}
      />
    );
  }

  // --- Focused editor ---
  if (mode === 'editor' && version) {
    return (
      <ConfigFocusedEditor
        draft={draft}
        isEditing={isEditing}
        onStartEdit={handleStartEdit}
      />
    );
  }

  // Fallback — still loading or redirecting
  return (
    <div className="page">
      <div style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>Loading…</div>
    </div>
  );
}
